import React, { useCallback, useMemo } from 'react';
import { hierarchy, Treemap } from '@visx/hierarchy';
import { withParentSize } from '@visx/responsive';
import { localPoint } from '@visx/event';
import { useTooltip } from '@visx/tooltip';
import { faChartNetwork } from '@fortawesome/pro-regular-svg-icons';
import { Icon } from 'js/components';
import QuestionSetNode from './QuestionSetNode';
import QuestionTooltip from './QuestionTooltip';
import { toFiniteNumber } from './rubricTreemapUtils';
import styles from './RubricTreemap.module.scss';

export const TREEMAP_HEIGHT = 320;

function RubricTreemapChart({
  parentWidth,
  questionSets,
  height = TREEMAP_HEIGHT,
  padding = 4,
  minLabelWidth = 20,
  minLabelHeight = 58,
}) {
  const width = Math.max(0, parentWidth || 0);
  const {
    tooltipData,
    tooltipLeft,
    tooltipTop,
    tooltipOpen,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const handleQuestionHover = useCallback(
    (event, questionData) => {
      const coords = localPoint(event.target.ownerSVGElement, event);
      if (!coords) {
        return;
      }
      showTooltip({
        tooltipLeft: coords.x,
        tooltipTop: coords.y,
        tooltipData: questionData,
      });
    },
    [showTooltip]
  );

  const handleQuestionSetHover = useCallback(
    (event, questionSetData) => {
      const coords = localPoint(event.target.ownerSVGElement, event);
      if (!coords) {
        return;
      }
      showTooltip({
        tooltipLeft: coords.x,
        tooltipTop: coords.y,
        tooltipData: questionSetData,
      });
    },
    [showTooltip]
  );

  const treeRoot = useMemo(() => {
    const weightedQuestionSets = (questionSets || [])
      .map((setNode) => ({
        ...setNode,
        areaWeight: toFiniteNumber(setNode.weight),
      }))
      .filter(
        (setNode) => setNode.areaWeight != null && setNode.areaWeight > 0
      );

    const totalRubricWeight = weightedQuestionSets.reduce(
      (sum, setNode) => sum + setNode.areaWeight,
      0
    );

    const children = weightedQuestionSets.map((setNode) => ({
      ...setNode,
      totalRubricWeight,
    }));

    return hierarchy({ name: 'question_sets', children })
      .sum((d) => d.areaWeight || 0)
      .sort((a, b) => b.value - a.value);
  }, [questionSets]);

  if (width <= 0) {
    return null;
  }

  const hasRenderableNodes = treeRoot.children?.length > 0;

  const minHeight = Math.max(120, height);
  const fullChartWidth = width + 2 * padding;

  if (!hasRenderableNodes) {
    return (
      <div className={styles['treemap-frame']} style={{ minHeight }}>
        <div className="media text-muted mt-4 mb-4">
          <Icon icon={faChartNetwork} size="4x" className="mr-3" fixedWidth />
          <div className="media-body">
            <p>
              This rubric doesn't have any active sources. Retired or
              zero-weight measurements are not included.
            </p>
            <p>Update your rubric configuration.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles['treemap-frame']}
      style={{
        minHeight,
        margin: `0 -${padding}px`,
      }}
    >
      <svg
        width={fullChartWidth}
        height={height}
        viewBox={`0 0 ${fullChartWidth} ${height}`}
      >
        <Treemap
          root={treeRoot}
          size={[fullChartWidth, height]}
          padding={padding}
          round
        >
          {(treemap) => (
            <g>
              {treemap
                .descendants()
                .filter((node) => !node.children)
                .map((node) => (
                  <QuestionSetNode
                    key={
                      node.data.id ||
                      node.data.key ||
                      node.data.name ||
                      `${node.x0}-${node.y0}`
                    }
                    node={node}
                    minLabelWidth={minLabelWidth}
                    minLabelHeight={minLabelHeight}
                    onQuestionHover={handleQuestionHover}
                    onQuestionLeave={hideTooltip}
                    onQuestionSetHover={handleQuestionSetHover}
                    onQuestionSetLeave={hideTooltip}
                  />
                ))}
            </g>
          )}
        </Treemap>
      </svg>
      <QuestionTooltip
        tooltipOpen={tooltipOpen}
        tooltipData={tooltipData}
        tooltipLeft={tooltipLeft}
        tooltipTop={tooltipTop}
      />
    </div>
  );
}

export default withParentSize(RubricTreemapChart);
